/**
 * Bowling scoring and display logic for the live viewer.
 * Mirrors BowlingDisplayRules.swift from the iOS app.
 */

/**
 * Get display text for a single roll in a frame.
 * @param {Object} frame - { number, rolls: [{ pinsDown, scoreValue, ballName?, ballImageUrl? }] }
 * @param {number} rollIndex - 0-based index of the roll within the frame
 * @param {boolean} isNoTap - whether 9-pin no-tap mode is active
 * @returns {string} Display symbol: "X", "/", "-", or digit
 */
export function rollText(frame, rollIndex, isNoTap = false) {
  if (!frame.rolls || rollIndex >= frame.rolls.length) return '';
  const val = frame.rolls[rollIndex].scoreValue;

  if (frame.number === 10) {
    // 10th frame
    if (val === 10) return 'X';

    // No-tap strike check (9 on fresh rack)
    if (isNoTap && val === 9) {
      if (rollIndex === 0) return 'X';
      if (rollIndex === 1) {
        const r0 = frame.rolls[0].scoreValue;
        if (r0 === 10 || (isNoTap && r0 === 9)) return 'X';
      }
      if (rollIndex === 2) {
        const r1 = frame.rolls[1].scoreValue;
        if (r1 === 10 || (isNoTap && r1 === 9)) return 'X';
        const r0 = frame.rolls[0].scoreValue;
        if (r0 + r1 === 10) return 'X';
      }
    }

    // Spare logic
    if (rollIndex === 1) {
      const prev = frame.rolls[0].scoreValue;
      if (prev < 10 && !(isNoTap && prev === 9) && prev + val === 10) return '/';
    }
    if (rollIndex === 2) {
      const r0 = frame.rolls[0].scoreValue;
      const r1 = frame.rolls[1].scoreValue;
      if ((r0 === 10 || (isNoTap && r0 === 9)) && r1 < 10 && !(isNoTap && r1 === 9) && r1 + val === 10) {
        return '/';
      }
    }
    return val === 0 ? '-' : `${val}`;
  }

  // Frames 1-9
  if (rollIndex === 0) {
    if (val === 10) return 'X';
    if (isNoTap && val === 9) return 'X';
    return val === 0 ? '-' : `${val}`;
  }
  // Second ball
  const prev = frame.rolls[0].scoreValue;
  if (prev + val === 10) return '/';
  return val === 0 ? '-' : `${val}`;
}

/**
 * Returns true if the given roll index in the 10th frame is on a fresh rack.
 * Mirrors Frame.isFreshRack10th from iOS.
 * @param {Object} frame - { number, rolls: [{ scoreValue }] }
 * @param {number} rollIndex - 0-based index within the 10th frame
 * @param {boolean} isNoTap
 * @returns {boolean}
 */
export function isFreshRack10th(frame, rollIndex, isNoTap) {
  if (!frame || !frame.rolls || rollIndex >= frame.rolls.length) return false;
  if (rollIndex === 0) return true;
  if (rollIndex === 1) {
    const r0 = frame.rolls[0].scoreValue;
    return r0 === 10 || (isNoTap && r0 === 9);
  }
  if (rollIndex === 2) {
    const r1 = frame.rolls[1].scoreValue;
    if (r1 === 10 || (isNoTap && r1 === 9)) return true;
    const r0 = frame.rolls[0].scoreValue;
    const ball2WasFresh = r0 === 10 || (isNoTap && r0 === 9);
    if (ball2WasFresh) return false; // ball 2 on fresh rack but didn't strike; ball 3 picks up leave
    return r0 + r1 === 10; // spare: ball 3 on fresh rack
  }
  return false;
}

/**
 * Calculate running scores for a game's frames.
 * In no-tap mode, 9 pins on the first ball of a frame counts as a strike worth 10.
 * @param {Array} frames - array of { number, rolls: [{ scoreValue }] }
 * @param {boolean} isNoTap
 * @returns {Array} array of running score numbers (null for incomplete frames)
 */
export function calculateRunningScores(frames, isNoTap = false) {
  const scores = new Array(10).fill(null);

  // Flatten all rolls with frame context (including rollIndex) for look-ahead
  const allRolls = [];
  for (const frame of frames) {
    if (!frame.rolls) continue;
    for (let ri = 0; ri < frame.rolls.length; ri++) {
      allRolls.push({
        scoreValue: frame.rolls[ri].scoreValue,
        frameNumber: frame.number,
        rollIndex: ri,
        frame,
      });
    }
  }

  // Returns the effective scoring value of a roll entry.
  // In no-tap mode, 9 pins on the first ball of any frame counts as 10.
  // For the 10th frame's second ball, also promotes 9→10 if on a fresh rack.
  function effectiveValue(rollEntry) {
    if (!isNoTap || rollEntry.scoreValue !== 9) return rollEntry.scoreValue;
    if (rollEntry.rollIndex === 0) return 10; // first ball of any frame: no-tap strike
    // 10th frame ball 2: promote if on a fresh rack (ball 1 was a strike)
    if (rollEntry.frameNumber === 10 && rollEntry.rollIndex === 1) {
      const r0 = rollEntry.frame.rolls[0]?.scoreValue;
      if (r0 === 10 || r0 === 9) return 10; // r0===9 means no-tap (isNoTap already true)
    }
    return rollEntry.scoreValue;
  }

  let rollIdx = 0;
  let runningTotal = 0;

  for (let f = 0; f < 10; f++) {
    const frameNum = f + 1;
    if (rollIdx >= allRolls.length) break;

    if (frameNum === 10) {
      // 10th frame: sum all remaining rolls, promoting 9s on fresh racks to 10
      const tenthFrame = frames[9];
      if (!tenthFrame || !tenthFrame.rolls) break;

      let frameScore = 0;
      let rollCount = 0;
      for (let ri = 0; ri < tenthFrame.rolls.length; ri++) {
        let val = tenthFrame.rolls[ri].scoreValue;
        if (isNoTap && val === 9 && isFreshRack10th(tenthFrame, ri, isNoTap)) {
          val = 10;
        }
        frameScore += val;
        rollIdx++;
        rollCount++;
      }

      // 10th frame needs at least 2 rolls (or 3 if strike/spare earned bonus)
      const firstVal = tenthFrame.rolls[0]?.scoreValue;
      const secondVal = tenthFrame.rolls[1]?.scoreValue;
      const isStrikeOrSpare = firstVal === 10 || (isNoTap && firstVal === 9) || (firstVal + (secondVal || 0) === 10);
      const neededRolls = isStrikeOrSpare ? 3 : 2;
      if (rollCount >= neededRolls) {
        runningTotal += frameScore;
        scores[f] = runningTotal;
      }
    } else {
      const firstRoll = allRolls[rollIdx];
      const firstVal = firstRoll.scoreValue;
      const isStrike = firstVal === 10 || (isNoTap && firstVal === 9);

      if (isStrike) {
        // Strike: frame scores 10 + next 2 bonus rolls (both promoted if no-tap first balls)
        if (rollIdx + 2 < allRolls.length) {
          const bonus1 = effectiveValue(allRolls[rollIdx + 1]);
          const bonus2 = effectiveValue(allRolls[rollIdx + 2]);
          runningTotal += 10 + bonus1 + bonus2;
          scores[f] = runningTotal;
        }
        rollIdx += 1;
      } else if (rollIdx + 1 < allRolls.length) {
        const secondVal = allRolls[rollIdx + 1].scoreValue;
        const isSpare = firstVal + secondVal === 10;
        if (isSpare) {
          // Spare: need next 1 bonus roll (promoted if no-tap first ball)
          if (rollIdx + 2 < allRolls.length) {
            const bonus = effectiveValue(allRolls[rollIdx + 2]);
            runningTotal += firstVal + secondVal + bonus;
            scores[f] = runningTotal;
          }
        } else {
          runningTotal += firstVal + secondVal;
          scores[f] = runningTotal;
        }
        rollIdx += 2;
      } else {
        rollIdx += 1;
      }
    }
  }

  return scores;
}

/**
 * Detect if standing pins form a split.
 * Mirrors BowlingHelpers.isSplit() from iOS.
 * A split requires: headpin (1) is down, at least 2 pins standing,
 * and there's a gap between standing pin columns.
 * @param {Array} pinsDown - array of pin numbers knocked down (1-10)
 * @returns {boolean}
 */
export function isSplit(pinsDown) {
  if (!pinsDown || pinsDown.length === 0) return false;
  // Headpin must be down for a split
  if (!pinsDown.includes(1)) return false;
  const standing = [];
  for (let p = 1; p <= 10; p++) {
    if (!pinsDown.includes(p)) standing.push(p);
  }
  if (standing.length < 2) return false;

  // Pin-to-column mapping (bowling pin layout)
  const pinColumns = { 7: 1, 4: 2, 2: 3, 8: 3, 1: 4, 5: 4, 3: 5, 9: 5, 6: 6, 10: 7 };
  const cols = standing.map(p => pinColumns[p]).filter(c => c !== undefined).sort((a, b) => a - b);

  for (let i = 0; i < cols.length - 1; i++) {
    if (cols[i + 1] - cols[i] > 1) return true; // gap between columns = split
  }
  return false;
}

/**
 * Calculate the maximum possible score for an in-progress game.
 * Assumes strikes for all remaining frames/rolls.
 * @param {Array} frames - array of { number, rolls: [{ scoreValue }] }
 * @param {boolean} isNoTap
 * @returns {number|null} max possible score, or null if game is complete or has no frames
 */
export function calculateMaxScore(frames, isNoTap = false) {
  if (!frames || frames.length === 0) return null;

  // Build a flat list of actual roll values
  const actualRolls = [];
  for (const frame of frames) {
    if (!frame.rolls) continue;
    for (const roll of frame.rolls) {
      actualRolls.push(roll.scoreValue);
    }
  }

  // Walk through frames consuming actual rolls, then pad remaining with strikes.
  // We build a complete simulated roll sequence frame-by-frame.
  const simRolls = [];
  let actualIdx = 0;

  for (let f = 0; f < 10; f++) {
    if (f === 9) {
      // 10th frame: up to 3 rolls
      for (let r = 0; r < 3; r++) {
        if (actualIdx < actualRolls.length) {
          simRolls.push(actualRolls[actualIdx++]);
        } else {
          simRolls.push(10); // pad with strike
        }
      }
    } else {
      if (actualIdx < actualRolls.length) {
        const firstVal = actualRolls[actualIdx];
        simRolls.push(firstVal);
        actualIdx++;

        const isStrike = firstVal === 10 || (isNoTap && firstVal === 9);
        if (!isStrike) {
          // Need second ball
          if (actualIdx < actualRolls.length) {
            simRolls.push(actualRolls[actualIdx++]);
          } else {
            // Pad: best case is a spare (knock down remaining)
            simRolls.push(10 - firstVal);
          }
        }
      } else {
        // Entire frame is future — pad with strike
        simRolls.push(10);
      }
    }
  }

  // Score the simulated complete game with no-tap promotion.
  // simRolls is flat: each frame's first ball is at the top of the frame's slot.
  // When scoring, any 9 that appears as a first ball (of a frame) in no-tap = 10.
  let score = 0;
  let idx = 0;

  for (let f = 0; f < 10; f++) {
    if (idx >= simRolls.length) break;

    if (f === 9) {
      // 10th frame: sum up to 3 rolls with fresh-rack promotion
      const b0raw = simRolls[idx] || 0;
      const b1raw = simRolls[idx + 1] || 0;
      const b2raw = simRolls[idx + 2] || 0;
      // Ball 0: always fresh rack
      const b0 = isNoTap && b0raw === 9 ? 10 : b0raw;
      // Ball 1: fresh rack if ball 0 was a strike
      const b0IsStrike = b0 === 10;
      const b1 = (isNoTap && b1raw === 9 && b0IsStrike) ? 10 : b1raw;
      // Ball 2: fresh rack if ball 1 was a strike on a fresh rack, or ball 0+1 = spare
      const b1IsStrikeOnFresh = b0IsStrike && b1 === 10;
      const isSpare = !b0IsStrike && b0raw + b1raw === 10;
      const b2 = (isNoTap && b2raw === 9 && (b1IsStrikeOnFresh || isSpare)) ? 10 : b2raw;
      score += b0 + b1 + b2;
    } else {
      const rawFirst = simRolls[idx];
      // In no-tap, first ball of every frame: 9 → 10
      const first = isNoTap && rawFirst === 9 ? 10 : rawFirst;
      const isStrike = first === 10;

      if (isStrike) {
        const rawB1 = simRolls[idx + 1] || 0;
        // b1 is first ball of the next frame → promote 9 in no-tap
        const b1 = isNoTap && rawB1 === 9 ? 10 : rawB1;
        const rawB2 = simRolls[idx + 2] || 0;
        // b2 is first ball of frame f+2 if b1 was also a strike, else second ball of f+1
        const b1IsStrike = b1 === 10;
        const b2 = (isNoTap && rawB2 === 9 && b1IsStrike) ? 10 : rawB2;
        score += first + b1 + b2;
        idx += 1;
      } else {
        const second = simRolls[idx + 1] || 0;
        const isSpare = rawFirst + second === 10;
        if (isSpare) {
          const rawB2 = simRolls[idx + 2] || 0;
          // b2 is first ball of next frame → promote 9 in no-tap
          const b2 = isNoTap && rawB2 === 9 ? 10 : rawB2;
          score += rawFirst + second + b2;
        } else {
          score += rawFirst + second;
        }
        idx += 2;
      }
    }
  }

  return score;
}
